import * as Sentry from "@sentry/react";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import { SupabaseClient } from "@supabase/supabase-js";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { analytics } from "./lib/analytics";

// Initialize Sentry for error tracking and performance monitoring
const isSentryEnabled =
  import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN;

if (isSentryEnabled) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      // Supabase integration - instruments all Supabase operations in the browser
      supabaseIntegration(SupabaseClient, Sentry, {
        tracing: true, // Track performance of auth and database operations
        breadcrumbs: true, // Add breadcrumbs for Supabase calls
        errors: true, // Capture Supabase errors
      }),
    ],
    // Performance monitoring
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 1.0,
    // Session replay sampling
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Environment and release tracking
    environment: import.meta.env.DEV ? "development" : "production",
    // beforeSend(event) {
    //   // Don't send events in dev unless specifically needed
    //   if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_SEND_IN_DEV) {
    //     console.log("Sentry event (not sent in dev):", event);
    //     return null;
    //   }
    //   return event;
    // },
  });
}

// Initialize PostHog for product analytics
const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
if (posthogKey) {
  analytics.init(posthogKey, "https://d.tendrel.io");
}

// Reload when a lazy-loaded chunk fails to load (e.g. a tab opened before a
// deploy requests chunk filenames that no longer exist). The timestamp guard
// lets the error surface instead of looping if the reload doesn't fix it.
const CHUNK_RELOAD_KEY = "chunk_load_error_reloaded_at";
const CHUNK_RELOAD_COOLDOWN_MS = 10_000;
window.addEventListener("vite:preloadError", () => {
  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const app = <App />;

createRoot(rootElement).render(
  <StrictMode>
    {isSentryEnabled ? (
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <h1>Something went wrong</h1>
            {import.meta.env.DEV && (
              <pre style={{ textAlign: "left", overflow: "auto" }}>
                {error instanceof Error ? error.message : String(error)}
              </pre>
            )}
            <button type="button" onClick={resetError}>
              Try again
            </button>
          </div>
        )}
        showDialog={import.meta.env.PROD}
      >
        {app}
      </Sentry.ErrorBoundary>
    ) : (
      app
    )}
  </StrictMode>,
);
